/** Validações por regra que a IA não substitui: CPF, CNPJ, CEP, nomes, capital e quotas. */
export function somenteDigitos(valor) {
    return (valor ?? '').replace(/\D+/g, '');
}
export function validarCpf(cpf) {
    const d = somenteDigitos(cpf);
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d))
        return false;
    const calc = (base, fator) => {
        let soma = 0;
        for (const ch of base)
            soma += Number(ch) * fator--;
        const resto = (soma * 10) % 11;
        return resto === 10 ? 0 : resto;
    };
    return calc(d.slice(0, 9), 10) === Number(d[9]) && calc(d.slice(0, 10), 11) === Number(d[10]);
}
export function validarCnpj(cnpj) {
    // CNPJ alfanumérico (Receita, desde jul/2026) usa o valor ASCII - 48 de cada caractere.
    const limpo = (cnpj ?? '').replace(/[.\-/\s]/g, '').toUpperCase();
    if (limpo.length !== 14 || !/^[A-Z0-9]{12}\d{2}$/.test(limpo))
        return false;
    if (/^(.)\1{13}$/.test(limpo))
        return false;
    const valor = (ch) => ch.charCodeAt(0) - 48;
    const calc = (base) => {
        const pesos = base.length === 12
            ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
            : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        let soma = 0;
        for (let i = 0; i < base.length; i++)
            soma += valor(base[i]) * pesos[i];
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };
    const dv1 = calc(limpo.slice(0, 12));
    const dv2 = calc(limpo.slice(0, 12) + dv1);
    return Number(limpo[12]) === dv1 && Number(limpo[13]) === dv2;
}
export function validarCep(cep) {
    return /^\d{8}$/.test(somenteDigitos(cep));
}
export function formatarCpf(cpf) {
    const d = somenteDigitos(cpf);
    return d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : cpf;
}
export function formatarCep(cep) {
    const d = somenteDigitos(cep);
    return d.length === 8 ? d.replace(/(\d{5})(\d{3})/, '$1-$2') : cep;
}
/** Normaliza para comparar nomes entre documentos: sem acento, caixa alta, espaços únicos. */
export function normalizarNome(nome) {
    return (nome ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
}
export function nomesEquivalentes(a, b) {
    const na = normalizarNome(a);
    const nb = normalizarNome(b);
    if (!na || !nb)
        return false;
    if (na === nb)
        return true;
    // Tolerar abreviações de nomes do meio: "JOSE C SILVA" ~ "JOSE CARLOS SILVA"
    const pa = na.split(' ');
    const pb = nb.split(' ');
    if (pa[0] !== pb[0] || pa[pa.length - 1] !== pb[pb.length - 1])
        return false;
    const meioA = pa.slice(1, -1);
    const meioB = pb.slice(1, -1);
    const [curto, longo] = meioA.length <= meioB.length ? [meioA, meioB] : [meioB, meioA];
    let j = 0;
    for (const parte of curto) {
        while (j < longo.length && !longo[j].startsWith(parte.replace('.', '')))
            j++;
        if (j >= longo.length)
            return false;
        j++;
    }
    return true;
}
/** Capital social não pode ter centavos; quotas não podem ser quebradas (motivo frequente de exigência). */
export function validarCapital(capitalCentavos) {
    if (!Number.isInteger(capitalCentavos) || capitalCentavos <= 0)
        return 'Capital deve ser positivo.';
    if (capitalCentavos % 100 !== 0)
        return 'Capital social não pode ter centavos.';
    return null;
}
export function validarQuotas(percentuais) {
    if (percentuais.length === 0)
        return 'Informe a participação de cada sócio.';
    const soma = percentuais.reduce((a, b) => a + b, 0);
    if (Math.abs(soma - 100) > 0.001)
        return `A soma das participações é ${soma}%, precisa ser 100%.`;
    if (percentuais.some((p) => p <= 0))
        return 'Cada sócio precisa ter participação maior que zero.';
    return null;
}
/** Divide o capital em quotas de R$ 1,00 e verifica se cada sócio recebe número inteiro. */
export function quotasInteiras(capitalCentavos, percentuais) {
    const quotas = capitalCentavos / 100;
    return percentuais.every((p) => Number.isInteger((quotas * p) / 100 + 0) || Math.abs(((quotas * p) / 100) % 1) < 1e-9);
}
export function validarCnae(codigo) {
    return /^\d{4}-?\d\/?\d{2}$/.test(codigo.trim());
}
export function normalizarCnae(codigo) {
    const d = somenteDigitos(codigo);
    return d.length === 7 ? `${d.slice(0, 4)}-${d[4]}/${d.slice(5)}` : codigo.trim();
}
export function validarTelefoneBr(telefone) {
    const d = somenteDigitos(telefone);
    return (d.length === 10 ||
        d.length === 11 ||
        (d.startsWith('55') && (d.length === 12 || d.length === 13)));
}
/** Telefone em E.164 para o WhatsApp (55 + DDD + número). */
export function telefoneE164(telefone) {
    let d = somenteDigitos(telefone);
    if (!d)
        return null;
    if (!d.startsWith('55'))
        d = `55${d}`;
    return d.length === 12 || d.length === 13 ? `+${d}` : null;
}
//# sourceMappingURL=validadores.js.map